import React, { useEffect } from "react";
import DatePicker from "react-datepicker";
import { Button } from "react-bootstrap";
import { FaSearch } from "react-icons/fa";
import formatDate from "../../utils/formatDate";
import { getDatePresetRange } from "./utils";

const selectStyle = {
  fontSize: "14px",
  padding: "0 36px 0 14px",
  height: "45px",
  minWidth: "150px",
  color: "#064260",
  border: "none",
  borderRadius: "8px",
  fontWeight: 500,
  appearance: "none",
  WebkitAppearance: "none",
  MozAppearance: "none",
  ["--bs-form-select-bg-img"]: "none",
  background: `#E9EEF1 url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12' fill='none'%3E%3Cpath d='M2 4L6 8L10 4' stroke='%23064260' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") no-repeat right 14px center / 12px 12px`,
};

const SessionFilters = ({
  search,
  setSearch,
  clearSearchFilter,
  selectedStartDate,
  selectedEndDate,
  showDatePicker,
  setShowDatePicker,
  handleRangeSelect,
  paymentMethodFilter,
  setPaymentMethodFilter,
  statusFilter,
  setStatusFilter,
  setCurrentPage,
  insuranceStatusFilter,
  setInsuranceStatusFilter,
  showBillingDetails = false,
  appointmentStatusFilter = "All",
  setAppointmentStatusFilter,
  datePreset = "All",
  setDatePreset,
}) => {
  useEffect(() => {
    if (selectedStartDate && selectedEndDate) {
      setShowDatePicker(false);
    }
  }, [selectedStartDate, selectedEndDate, setShowDatePicker]);

  const handleDatePresetChange = (value) => {
    setDatePreset?.(value);
    setCurrentPage(1);

    if (value === "All") {
      handleRangeSelect(null);
      setShowDatePicker(false);
      return;
    }

    if (value === "Custom") {
      setShowDatePicker(true);
      return;
    }

    const range = getDatePresetRange(value);
    if (range) {
      handleRangeSelect([range.start, range.end]);
      setShowDatePicker(false);
    }
  };

  const dateButtonLabel = () => {
    if (datePreset && datePreset !== "All" && datePreset !== "Custom") {
      return datePreset;
    }
    if (selectedStartDate && selectedEndDate) {
      return `${formatDate(selectedStartDate)} - ${formatDate(selectedEndDate)}`;
    }
    if (selectedStartDate) {
      return formatDate(selectedStartDate);
    }
    return "Date";
  };

  return (
    <div
      className="d-flex gap-2 align-items-center"
      style={{ flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}
    >
      <div className="position-relative" style={{ width: "160px", minWidth: "140px" }}>
        <div className="app-filter-search-wrap" style={{ width: "100%" }}>
          <FaSearch className="app-filter-search-icon" />
          <input
            className="form-control app-filter-search"
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search"
            style={{
              width: "100%",
              paddingRight: search ? "30px" : undefined,
            }}
            type="text"
            value={search}
          />
        </div>

        {search && (
          <i
            className="fas fa-times position-absolute"
            onClick={clearSearchFilter}
            style={{
              color: "#dc3545",
              cursor: "pointer",
              fontSize: "12px",
              right: "10px",
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 2,
            }}
          />
        )}
      </div>

      <div
        className="position-relative date-picker-container"
        style={{
          minWidth: selectedStartDate && selectedEndDate ? "200px" : "150px",
          width: selectedStartDate && selectedEndDate ? "200px" : "150px",
        }}
      >
        <select
          className="form-select custom-select app-filter-select"
          value={datePreset || "All"}
          onChange={(e) => handleDatePresetChange(e.target.value)}
          style={{ ...selectStyle, minWidth: "150px", width: "100%" }}
        >
          <option value="All">Date</option>
          <option value="Today">Today</option>
          <option value="Tomorrow">Tomorrow</option>
          <option value="This Week">This Week</option>
          <option value="Next 7 Days">Next 7 Days</option>
          <option value="Custom">Custom Date Range</option>
        </select>

        {(selectedStartDate || selectedEndDate) && (
          <Button
            className="custom-date-button"
            onClick={() => {
              handleRangeSelect(null);
              setDatePreset?.("All");
              setShowDatePicker(false);
              setCurrentPage(1);
            }}
            variant="outline-secondary"
            title={dateButtonLabel()}
            style={{
              display: "none",
            }}
          />
        )}

        {showDatePicker && (
          <div
            className="position-absolute"
            style={{ right: 0, top: "100%", zIndex: 1000, marginTop: "4px" }}
          >
            <DatePicker
              endDate={selectedEndDate}
              inline
              isClearable={true}
              onChange={(dates) => {
                handleRangeSelect(dates);
                setDatePreset?.("Custom");
                if (dates && Array.isArray(dates) && dates[0] && dates[1]) {
                  setShowDatePicker(false);
                }
              }}
              selectsRange
              startDate={selectedStartDate}
            />
          </div>
        )}
      </div>

      {!showBillingDetails && setAppointmentStatusFilter && (
        <select
          className="form-select custom-select app-filter-select"
          onChange={(e) => {
            setAppointmentStatusFilter(e.target.value);
            setCurrentPage(1);
          }}
          value={appointmentStatusFilter}
          style={{ ...selectStyle, minWidth: "165px" }}
        >
          <option value="All">Appointment Status</option>
          <option value="Scheduled">Scheduled</option>
          <option value="User Cancel">User Cancel</option>
          <option value="Provider Cancel">Provider Cancel</option>
          <option value="Late Cancel">Late Cancel</option>
        </select>
      )}

      <select
        className="form-select custom-select app-filter-select"
        onChange={(e) => {
          setPaymentMethodFilter(e.target.value);
          setCurrentPage(1);
        }}
        value={paymentMethodFilter}
        style={{ ...selectStyle, minWidth: "155px" }}
      >
        <option value="All">Payment Method</option>
        <option value="Insurance">Insurance</option>
        <option value="Self-Pay">Self-Pay</option>
        <option value="Access Code">Access Code</option>
      </select>

      <select
        className="form-select custom-select app-filter-select"
        onChange={(e) => {
          setStatusFilter(e.target.value);
          setCurrentPage(1);
        }}
        value={statusFilter}
        style={{
          ...selectStyle,
          minWidth: showBillingDetails ? "160px" : "145px",
        }}
      >
        {showBillingDetails ? (
          <>
            <option value="All">Payment Status</option>
            <option value="Verified">Paid</option>
            <option value="Invalid">Failed</option>
          </>
        ) : (
          <>
            <option value="All">Payment Status</option>
            <option value="Verified">Verified</option>
            <option value="Pending">Pending</option>
            <option value="Invalid">Invalid</option>
          </>
        )}
      </select>

      {showBillingDetails && (
        <select
          className="form-select custom-select app-filter-select"
          onChange={(e) => {
            setInsuranceStatusFilter(e.target.value);
            setCurrentPage(1);
          }}
          value={insuranceStatusFilter}
          style={{ ...selectStyle, minWidth: "165px" }}
        >
          <option value="All">Insurance Status</option>
          <option value="paid">Paid</option>
          <option value="billed">Billed</option>
          <option value="unbilled">Unbilled</option>
        </select>
      )}
    </div>
  );
};

export default SessionFilters;
